/**
 * Genesis — API bindings + shared types.
 *
 * Every call carries the Genesis session id via a dedicated header so the
 * backend cost guard forces downstream reservations into simulation mode.
 */
import { http } from "@/shared/api/http";

export type GenesisPhase =
  | "welcome"
  | "discovery"
  | "demo_department"
  | "make_this_mine"
  | "intelly_conversation"
  | "logo"
  | "transformation"
  | "briefing"
  | "complete";

export const PHASE_ORDER: readonly GenesisPhase[] = [
  "welcome",
  "discovery",
  "demo_department",
  "make_this_mine",
  "intelly_conversation",
  "logo",
  "transformation",
  "briefing",
  "complete",
] as const;

export interface IntellyAnswers {
  business_name?: string;
  website?: string;
  location?: string;
  service_area?: string;
  products_services?: string;
  ideal_customer?: string;
  brand_personality?: string;
  existing_social_accounts?: Record<string, string>;
  mission?: string;
  additional_context?: string;
  has_logo?: boolean;
}

export interface GenesisSession {
  _id: string;
  user_id: string;
  current_phase: GenesisPhase;
  completed_phases: GenesisPhase[];
  industry_key: string | null;
  custom_description: string | null;
  classification_result: {
    industry_key: string | null;
    industry_label: string | null;
    confidence: number;
    matched_keywords: string[];
    tags: string[];
  } | null;
  demo_key: string | null;
  demo_visited_paths: string[];
  answers: IntellyAnswers;
  selected_logo_concept: string | null;
  workspace_id: string | null;
  briefing: BriefingPayload | null;
  time_in_demo_seconds: number;
  time_to_make_this_mine_seconds: number | null;
  drop_off_phase: GenesisPhase | null;
}

export interface Industry {
  key: string;
  label: string;
  demo_key: string;
  tags: string[];
}

export interface IndustryCatalog {
  categories: { name: string; industries: Industry[] }[];
  total: number;
}

export interface DemoSummary {
  key: string;
  display_name: string;
  industry_label: string;
  hero_tagline: string;
  color_accent: string;
}

export interface DemoFullShape {
  key: string;
  display_name: string;
  industry_label: string;
  hero_tagline: string;
  color_accent: string;
  brand_brain: {
    voice: string;
    tagline: string;
    identity_pillars: string[];
    do_use: string[];
    dont_use: string[];
    colors: Record<string, string>;
    typography: Record<string, string>;
  };
  business_brain: {
    mission: string;
    ideal_customer: string;
    core_offer: string;
    unique_advantage: string;
    revenue_model: string;
    growth_stage: string;
  };
  campaigns: {
    id: string; name: string; objective: string; status: string;
    channels: string[]; starts: string; ends: string; ai_summary: string;
  }[];
  assets: {
    id: string; campaign_id: string; kind: string; title: string;
    body: string; status: string; channel: string;
  }[];
  calendar: {
    id: string; asset_id: string; when: string; channel: string; status: string;
  }[];
  knowledge: {
    id: string; source: string; title: string; excerpt: string; tags: string[];
  }[];
  insights: {
    id: string; title: string; body: string; priority: "high" | "medium" | "low";
  }[];
  logo_concepts: {
    id: string; label: string; svg: string; style_tags: string[];
  }[];
}

export interface ClassificationOutcome {
  industry_key: string | null;
  industry_label: string | null;
  confidence: number;
  matched_keywords: string[];
  tags: string[];
}

export interface BriefingPayload {
  business_name: string;
  industry_label: string;
  demo_source: string;
  created_deliverables: { kind: string; count: number }[];
  observations: { id: string; title: string; body: string; priority: "high" | "medium" | "low" }[];
  next_recommended_actions: { key: string; label: string }[];
  workspace_id: string;
}

const opts = (sid?: string | null) => (sid ? { genesisSessionId: sid } : {});

export const startSession = (): Promise<GenesisSession> =>
  http<GenesisSession>("/genesis/session/start", { method: "POST" });

export const getSession = (id: string): Promise<GenesisSession> =>
  http<GenesisSession>(`/genesis/session/${id}`, opts(id));

export const advancePhase = (id: string, to: GenesisPhase): Promise<GenesisSession> =>
  http<GenesisSession>(`/genesis/session/${id}/advance`, {
    method: "POST", body: { to_phase: to }, ...opts(id),
  });

export const recordAnalytics = (id: string, patch: Partial<{
  time_in_demo_seconds: number;
  time_to_make_this_mine_seconds: number;
  drop_off_phase: GenesisPhase;
  demo_visited_paths: string[];
}>): Promise<{ status: string }> =>
  http(`/genesis/session/${id}/analytics`, {
    method: "POST", body: patch, ...opts(id),
  });

export const industryCatalog = (): Promise<IndustryCatalog> =>
  http<IndustryCatalog>("/genesis/industries");

export const saveDiscovery = (
  id: string,
  payload: { industry_key?: string | null; custom_description?: string | null },
): Promise<{ session: GenesisSession; classification: ClassificationOutcome }> =>
  http(`/genesis/session/${id}/discovery`, {
    method: "POST", body: payload, ...opts(id),
  });

export const listDemos = (): Promise<{ demos: DemoSummary[] }> =>
  http<{ demos: DemoSummary[] }>("/genesis/demos");

export const previewDemo = (demoKey: string): Promise<DemoFullShape> =>
  http<DemoFullShape>(`/genesis/demos/${demoKey}`);

export const saveAnswers = (id: string, answers: IntellyAnswers): Promise<GenesisSession> =>
  http<GenesisSession>(`/genesis/session/${id}/answers`, {
    method: "POST", body: answers, ...opts(id),
  });

export const logoConcepts = (id: string): Promise<{
  concepts: { id: string; label: string; svg: string; style_tags: string[] }[];
}> =>
  http(`/genesis/session/${id}/logo/concepts`, opts(id));

export const selectLogo = (id: string, conceptId: string): Promise<GenesisSession> =>
  http<GenesisSession>(`/genesis/session/${id}/logo/select`, {
    method: "POST", body: { concept_id: conceptId }, ...opts(id),
  });

export const transform = async (id: string): Promise<BriefingPayload> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20_000);
  try {
    return await http<BriefingPayload>(`/genesis/session/${id}/transform`, {
      method: "POST", signal: controller.signal, ...opts(id),
    });
  } finally {
    window.clearTimeout(timeout);
  }
};

export const completeSession = (id: string): Promise<GenesisSession> =>
  http<GenesisSession>(`/genesis/session/${id}/complete`, {
    method: "POST", ...opts(id),
  });
